import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Rocket, MessageSquare, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505]">
        <span className="font-mono-j text-sm text-zinc-500">Loading…</span>
      </div>
    );
  }

  const cards = [
    { icon: Rocket, title: "Your Projects", desc: "Track active engagements and milestones in one place." },
    { icon: MessageSquare, title: "Messages", desc: "Chat directly with your Bluevera project team." },
    { icon: FileText, title: "Invoices & Docs", desc: "Access proposals, contracts and billing history." },
  ];

  return (
    <section className="min-h-screen pt-32 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-14">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border border-white/10" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-cyan-500/10 grid place-items-center text-cyan-400 font-display font-bold text-xl">
                {user.name?.[0] || "U"}
              </div>
            )}
            <div>
              <p className="font-mono-j text-xs tracking-[0.2em] uppercase text-cyan-400">Client Portal</p>
              <h1 className="font-display font-bold text-3xl sm:text-4xl text-white">Welcome, {user.name?.split(" ")[0] || "there"}</h1>
              <p className="text-zinc-500 text-sm mt-1">{user.email}</p>
            </div>
          </div>
          <button
            data-testid="dashboard-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 border border-white/15 text-white px-5 py-3 rounded-full hover:border-cyan-400 hover:text-cyan-400 transition-colors self-start"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6">
        {cards.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div className="p-8 rounded-2xl border border-white/10 bg-[#0A0A0A] h-full">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 grid place-items-center mb-6">
                <c.icon className="text-cyan-400" size={22} />
              </div>
              <h3 className="font-display font-semibold text-xl text-white">{c.title}</h3>
              <p className="mt-2 text-zinc-400 text-sm">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
